/**
 * Batch lifecycle helpers — upload-accept eligibility.
 *
 * Accepting a parse-clean upload bumps `data_version` AND re-runs the calc
 * (upserting calc_results). That used to restrict it to the pre-signature
 * states, because an Approved batch's figures were frozen and the post-commit
 * recompute would fail against them. Nothing freezes any more, so a late upload
 * lands on an approved batch like any other — the list is every state.
 *
 * Kept as an explicit ALLOW-list rather than collapsed to `true`: a future
 * unknown state should default to NOT acceptable rather than silently offering
 * an accept that fails server-side.
 *
 * NOTE: this is upload-accept eligibility specifically. Corrections have their
 * own editable-state rule in CorrectionsTab — don't conflate them.
 */
import type { BatchState, BatchSummary } from "../api/types";

const ACCEPTABLE_BATCH_STATES = new Set<string>(["Open", "PendingApproval", "Approved"]);

/** Whether a parse-clean upload can be accepted into a batch in this state. */
export function canAcceptUpload(state: BatchState): boolean {
  return ACCEPTABLE_BATCH_STATES.has(state);
}

/**
 * The states a recompute can run in — every state, since the backend stopped
 * refusing any of them.
 *
 * The members coincide with {@link ACCEPTABLE_BATCH_STATES} today, and the two
 * are deliberately kept apart anyway: they answer different questions and
 * derive from different backend rules, so one widening must not silently widen
 * the other. Kept as an explicit allow-list rather than collapsed to `true` for
 * the same reason as above — an unknown future state offers no recompute rather
 * than a button that only 409s.
 */
const RECOMPUTABLE_BATCH_STATES = new Set<string>(["Open", "PendingApproval", "Approved"]);

/** Whether a recompute is worth offering for a batch in this state. */
export function canRecomputeBatch(state: BatchState): boolean {
  return RECOMPUTABLE_BATCH_STATES.has(state);
}

/**
 * The "active" batch from a list: most-recent Open, else most-recent overall
 * (by period_start descending). The BE doesn't guarantee /batches order, so we
 * sort before picking — otherwise an operator could land in an older Open
 * period. Returns undefined for an empty list.
 *
 * Shared by useActiveBatchId and the upload/template pages' default-batch
 * pickers so the "which period am I working in" rule lives in one place.
 */
export function pickActiveBatch(items: ReadonlyArray<BatchSummary>): BatchSummary | undefined {
  // Order by parsed instant, not lexically: two period_start values with
  // different UTC offsets can sort lexically != chronologically, scoping the
  // operator to the wrong reporting period (BIO-144 #9; mirrors pickActiveUpload).
  const sorted = [...items].sort((a, b) => Date.parse(b.period_start) - Date.parse(a.period_start));
  return sorted.find((b) => b.state === "Open") ?? sorted[0];
}
